import { useState, useEffect } from "react";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";
import usePermiso from "../hooks/usePermiso";

const DEFAULTS = {
  smtp_host: "mail.vitalcasa.com",
  smtp_port: 587,
  smtp_user: "",
  smtp_pass: "",
  smtp_configured: false,
};

const SMTP_PASS_MASK = "••••••••••";

export default function ConexionCorreo() {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const veServidor = puede("cfg_correo_servidor", "ver");
  const modServidor = puede("cfg_correo_servidor", "modificar");
  const veUsuario = puede("cfg_correo_usuario", "ver");
  const modUsuario = puede("cfg_correo_usuario", "modificar");
  const vePass = puede("cfg_correo_pass", "ver");
  const modPass = puede("cfg_correo_pass", "modificar");
  const puedeGuardarAlgo = modServidor || modUsuario || modPass;

  const [form, setForm] = useState(DEFAULTS);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [testMsg, setTestMsg] = useState("");
  const [testOk, setTestOk] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/auth/me/correo");
        const d = res.data?.data || {};
        const configured = d.smtp_configured === true;
        setForm({
          smtp_host: d.smtp_host || DEFAULTS.smtp_host,
          smtp_port: d.smtp_port || DEFAULTS.smtp_port,
          smtp_user: d.smtp_user || d.email || "",
          smtp_pass: configured ? SMTP_PASS_MASK : "",
          smtp_configured: configured,
        });
      } catch (e) {
        setError(e.response?.data?.error || "No se pudo cargar la configuración de correo");
      } finally {
        setCargando(false);
      }
    })();
  }, []);

  const cambiar = (e) => {
    const { name, value } = e.target;
    setTestMsg("");
    setTestOk(null);
    setForm((prev) => {
      const next = { ...prev, [name]: name === "smtp_port" ? parseInt(value, 10) || 587 : value };
      return next;
    });
  };

  const passParaApi = () => {
    const v = String(form.smtp_pass || "").trim();
    if (!v || v === SMTP_PASS_MASK) return undefined;
    return v;
  };

  const probar = async () => {
    if (!puedeGuardarAlgo) return;
    setError("");
    setOkMsg("");
    setTestMsg("");
    setTestOk(null);
    setProbando(true);
    try {
      const res = await api.post("/auth/me/correo/probar", {
        smtp_host: form.smtp_host,
        smtp_port: form.smtp_port,
        smtp_user: form.smtp_user,
        smtp_pass: passParaApi() || "",
      });
      const d = res.data || {};
      setTestOk(true);
      setTestMsg(d.mensaje || "Correo correcto.");
    } catch (e) {
      const d = e.response?.data || {};
      setTestOk(false);
      setTestMsg(d.mensaje || d.error || "No se pudo comprobar el correo");
    } finally {
      setProbando(false);
    }
  };

  const guardar = async () => {
    if (!puedeGuardarAlgo) return;
    setError("");
    setOkMsg("");
    setGuardando(true);
    try {
      const res = await api.patch("/auth/me/correo", {
        smtp_host: form.smtp_host,
        smtp_port: form.smtp_port,
        smtp_user: form.smtp_user,
        smtp_pass: passParaApi(),
      });
      const d = res.data?.data || {};
      const configured = d.smtp_configured === true;
      setForm((prev) => ({
        ...prev,
        smtp_host: d.smtp_host || prev.smtp_host,
        smtp_port: d.smtp_port || prev.smtp_port,
        smtp_user: d.smtp_user || prev.smtp_user,
        smtp_pass: configured ? SMTP_PASS_MASK : "",
        smtp_configured: configured,
      }));
      setOkMsg("Configuración guardada. Ya puedes enviar emails desde el CRM.");
    } catch (e) {
      setError(e.response?.data?.error || "Error al guardar");
    } finally {
      setGuardando(false);
    }
  };

  const inp = {
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${c.border}`,
    background: c.bgCard,
    color: c.textPrimary,
    fontSize: 14,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  const lbl = { fontSize: 13, fontWeight: 600, color: c.textSecondary, marginBottom: 6, display: "block" };

  return (
    <div style={{ padding: 24, maxWidth: 640, margin: "0 auto" }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: c.textPrimary, margin: 0 }}>
          📧 Mi correo
        </h1>
        <p style={{ fontSize: 13, color: c.textMuted, margin: "6px 0 0", lineHeight: 1.45 }}>
          Buzón con el que envías emails desde el CRM (tickets, etc.).
        </p>
      </div>

      <div style={{
        background: c.bgCard,
        borderRadius: 12,
        border: `1px solid ${c.border}`,
        padding: 22,
      }}>
        {cargando ? (
          <p style={{ color: c.textMuted, fontSize: 14 }}>Cargando…</p>
        ) : (
          <>
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "7px 14px",
              borderRadius: 20,
              marginBottom: 20,
              background: form.smtp_configured ? "rgba(22,163,74,0.08)" : "rgba(245,158,11,0.08)",
              border: `1px solid ${form.smtp_configured ? "rgba(22,163,74,0.25)" : "rgba(245,158,11,0.25)"}`,
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: "50%",
                background: form.smtp_configured ? "#16a34a" : "#f59e0b",
              }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: form.smtp_configured ? "#16a34a" : "#f59e0b" }}>
                {form.smtp_configured ? "Correo listo para enviar" : "Falta guardar la contraseña del buzón"}
              </span>
            </div>

            <div style={{ display: "grid", gap: 16 }}>
              {veServidor && (
              <div>
                <label style={lbl}>Servidor SMTP</label>
                <input name="smtp_host" value={form.smtp_host} onChange={cambiar} style={inp} placeholder="mail.vitalcasa.com" disabled={!modServidor} title={!modServidor ? "No tienes permiso para modificar el servidor SMTP" : undefined} />
              </div>
              )}
              {veServidor && (
              <div>
                <label style={lbl}>Puerto</label>
                <input name="smtp_port" type="number" value={form.smtp_port} onChange={cambiar} style={inp} disabled={!modServidor} title={!modServidor ? "No tienes permiso para modificar el puerto SMTP" : undefined} />
              </div>
              )}
              {veUsuario && (
              <div>
                <label style={lbl}>Usuario / email del buzón</label>
                <input name="smtp_user" value={form.smtp_user} onChange={cambiar} style={inp} placeholder="tu@vitalcasa.com" disabled={!modUsuario} title={!modUsuario ? "No tienes permiso para modificar el usuario del buzón" : undefined} />
              </div>
              )}
              {vePass && (
              <div>
                <label style={lbl}>Contraseña del correo</label>
                <input
                  name="smtp_pass"
                  type="password"
                  value={form.smtp_pass}
                  onChange={cambiar}
                  onFocus={() => {
                    setForm((prev) => (
                      prev.smtp_pass === SMTP_PASS_MASK ? { ...prev, smtp_pass: "" } : prev
                    ));
                  }}
                  onBlur={() => {
                    setForm((prev) => (
                      prev.smtp_configured && !String(prev.smtp_pass || "").trim()
                        ? { ...prev, smtp_pass: SMTP_PASS_MASK }
                        : prev
                    ));
                  }}
                  style={inp}
                  placeholder={form.smtp_configured ? "Escribe solo si quieres cambiarla" : "Contraseña del buzón"}
                  autoComplete="new-password"
                  disabled={!modPass}
                  title={!modPass ? "No tienes permiso para modificar la contraseña del correo" : undefined}
                />
                {form.smtp_configured && (
                  <p style={{ fontSize: 11, color: c.textMuted, margin: "6px 0 0" }}>
                    ✓ Contraseña guardada (••••). Déjala así si no quieres cambiarla.
                  </p>
                )}
              </div>
              )}
            </div>

            {error && (
              <div style={{ marginTop: 16, padding: "12px 14px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", fontSize: 13 }}>
                ⚠️ {error}
              </div>
            )}
            {okMsg && (
              <div style={{ marginTop: 16, padding: "12px 14px", borderRadius: 8, background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.25)", color: "#16a34a", fontSize: 13 }}>
                ✓ {okMsg}
              </div>
            )}
            {testMsg && (
              <div style={{
                marginTop: 16,
                padding: "12px 14px",
                borderRadius: 8,
                fontSize: 13,
                lineHeight: 1.45,
                background: testOk ? "rgba(22,163,74,0.08)" : "rgba(239,68,68,0.08)",
                border: `1px solid ${testOk ? "rgba(22,163,74,0.25)" : "rgba(239,68,68,0.25)"}`,
                color: testOk ? "#16a34a" : "#dc2626",
              }}>
                {testOk ? "✓ " : "✗ "}{testMsg}
              </div>
            )}

            <div style={{ marginTop: 22, display: "flex", flexWrap: "wrap", gap: 12 }}>
              <button
                type="button"
                onClick={probar}
                disabled={probando || !puedeGuardarAlgo}
                title={!puedeGuardarAlgo ? "No tienes permiso para modificar la configuración de correo" : undefined}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: `1px solid ${c.border}`,
                  background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                  color: c.textPrimary,
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: (probando || !puedeGuardarAlgo) ? (probando ? "wait" : "not-allowed") : "pointer",
                  opacity: (probando || !puedeGuardarAlgo) ? 0.7 : 1,
                }}
              >
                {probando ? "Comprobando…" : "Probar correo (enviar + recibir)"}
              </button>
              <button
                type="button"
                onClick={guardar}
                disabled={guardando || !puedeGuardarAlgo}
                title={!puedeGuardarAlgo ? "No tienes permiso para modificar la configuración de correo" : undefined}
                style={{
                  padding: "10px 22px",
                  borderRadius: 8,
                  border: "none",
                  background: "#ea580c",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: (guardando || !puedeGuardarAlgo) ? (guardando ? "wait" : "not-allowed") : "pointer",
                  opacity: (guardando || !puedeGuardarAlgo) ? 0.7 : 1,
                }}
              >
                {guardando ? "Guardando…" : "Guardar configuración"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
